/**
 * Exact check of a packed sheet, independent of how the packer got there.
 *
 * The packers work on rasters (occupancy grids) or boxes. This takes the
 * finished placements, maps every piece's real silhouette through the SAME
 * placement frame the sheet builders draw with (placementFrame), grows it by
 * its outline extent plus half the padding, and tests every nearby pair with
 * an exact polygon intersection (Clipper), plus the fixed edge margin.
 *
 * Two halos that do not overlap mean the two pieces' outermost outlines are
 * at least the padding apart. So a sheet that passes cannot cut into another
 * piece, whatever the packer did, and a packer/renderer mismatch (a piece
 * drawn turned differently than it was packed) is caught too.
 */
import ClipperLib from 'clipper-lib';
import { flattenPathData, IDENTITY, type Mat } from './clipFlatten';
import {
  SHEET_EDGE_MARGIN_MM,
  outlineExtentPt,
  placementFrame,
  type NestSheet,
  type OutlineSpec,
} from './nesting';
import { mmToPt } from './units';

type IntPoint = { X: number; Y: number };
const SCALE = 1000;

/** Overlap below this (pt²) is numerical noise, not a collision. */
export const VERIFY_OVERLAP_PT2 = 0.5;
/** Border breaches below this (pt) are rounding, not a breach. */
const BORDER_SLACK_PT = 0.05;

export interface SheetViolation {
  kind: 'overlap' | 'border';
  a: string;
  b?: string;
  /** pt² for an overlap, pt for a border breach. */
  amount: number;
}

/** Local halo rings (piece-local pt) memoised per silhouette and growth. */
type LocalRings = Array<Array<[number, number]>>;

function offsetRings(rings: LocalRings, delta: number): LocalRings {
  const paths = rings.map((r) =>
    r.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) }))
  );
  const c = new ClipperLib.Clipper();
  c.AddPaths(paths, ClipperLib.PolyType.ptSubject, true);
  const unioned: IntPoint[][] = [];
  c.Execute(
    ClipperLib.ClipType.ctUnion,
    unioned,
    ClipperLib.PolyFillType.pftNonZero,
    ClipperLib.PolyFillType.pftNonZero
  );
  if (delta <= 0) return unioned.map((p) => p.map((q) => [q.X / SCALE, q.Y / SCALE]));
  const o = new ClipperLib.ClipperOffset(2, 0.1 * SCALE);
  o.AddPaths(unioned, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
  const out: IntPoint[][] = [];
  o.Execute(out, delta * SCALE);
  return out.map((p) => p.map((q) => [q.X / SCALE, q.Y / SCALE]));
}

/** Absolute area of an integer ring (shoelace). */
function ringArea(p: IntPoint[]): number {
  let twice = 0;
  for (let i = 0, n = p.length; i < n; i++) {
    const q = p[i];
    const r = p[(i + 1) % n];
    twice += q.X * r.Y - r.X * q.Y;
  }
  return Math.abs(twice) / 2;
}

function place(rings: LocalRings, m: Mat): IntPoint[][] {
  return rings.map((r) =>
    r.map(([x, y]) => ({
      X: Math.round((m[0] * x + m[2] * y + m[4]) * SCALE),
      Y: Math.round((m[1] * x + m[3] * y + m[5]) * SCALE),
    }))
  );
}

function bounds(paths: IntPoint[][]): [number, number, number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of paths) {
    for (const q of p) {
      if (q.X < x0) x0 = q.X;
      if (q.Y < y0) y0 = q.Y;
      if (q.X > x1) x1 = q.X;
      if (q.Y > y1) y1 = q.Y;
    }
  }
  return [x0 / SCALE, y0 / SCALE, x1 / SCALE, y1 / SCALE];
}

export interface VerifyContext {
  spec: OutlineSpec;
  paddingPt: number;
  paperWidthPt: number;
  paperLengthPt: number;
  /** Awaited between pairs so the UI keeps repainting. */
  pause?: () => Promise<void>;
  /** Shared across sheets of one run (silhouette flattening is the cost). */
  memo?: Map<string, { halo: LocalRings; outer: LocalRings } | null>;
}

/**
 * Every collision on the sheet: halos (outline extent + padding/2) of two
 * pieces overlapping, or a piece's outermost outline inside the edge margin.
 * Pieces without a silhouette are checked by their box.
 */
export async function verifySheet(
  sheet: Pick<NestSheet, 'placements'>,
  ctx: VerifyContext
): Promise<SheetViolation[]> {
  const memo = ctx.memo ?? new Map();
  const border = mmToPt(SHEET_EDGE_MARGIN_MM);
  const out: SheetViolation[] = [];
  const shapes: Array<{ id: string; halo: IntPoint[][]; box: [number, number, number, number] }> =
    [];
  for (const placement of sheet.placements) {
    const { piece } = placement;
    const extent = outlineExtentPt(piece, ctx.spec);
    const d = piece.outlineD ?? `M0 0h${piece.widthPt}v${piece.heightPt}h${-piece.widthPt}z`;
    const key = `${d}|${extent}|${ctx.paddingPt}`;
    let local = memo.get(key);
    if (local === undefined) {
      const flat = await flattenPathData(d, IDENTITY, 0.2);
      const rings: LocalRings = (flat ?? [])
        .filter((p) => p.points.length >= 3)
        .map((p) => p.points.map(([x, y]) => [x, y] as [number, number]));
      local = rings.length
        ? {
            halo: offsetRings(rings, extent + ctx.paddingPt / 2),
            outer: offsetRings(rings, extent),
          }
        : null;
      memo.set(key, local);
    }
    if (!local) continue; // unreadable silhouette: nothing exact to check
    const { matrix } = placementFrame(placement, ctx.spec);
    const halo = place(local.halo, matrix);
    const [ox0, oy0, ox1, oy1] = bounds(place(local.outer, matrix));
    const breach = Math.max(
      border - ox0,
      border - oy0,
      ox1 - (ctx.paperWidthPt - border),
      oy1 - (ctx.paperLengthPt - border)
    );
    if (breach > BORDER_SLACK_PT) out.push({ kind: 'border', a: piece.id, amount: breach });
    shapes.push({ id: piece.id, halo, box: bounds(halo) });
  }
  for (let i = 0; i < shapes.length; i++) {
    await ctx.pause?.();
    const A = shapes[i];
    for (let j = i + 1; j < shapes.length; j++) {
      const B = shapes[j];
      if (A.box[2] < B.box[0] || B.box[2] < A.box[0] || A.box[3] < B.box[1] || B.box[3] < A.box[1])
        continue;
      const c = new ClipperLib.Clipper();
      c.AddPaths(A.halo, ClipperLib.PolyType.ptSubject, true);
      c.AddPaths(B.halo, ClipperLib.PolyType.ptClip, true);
      const inter: IntPoint[][] = [];
      c.Execute(
        ClipperLib.ClipType.ctIntersection,
        inter,
        ClipperLib.PolyFillType.pftNonZero,
        ClipperLib.PolyFillType.pftNonZero
      );
      const area = inter.reduce((s, p) => s + ringArea(p), 0) / (SCALE * SCALE);
      if (area > VERIFY_OVERLAP_PT2) out.push({ kind: 'overlap', a: A.id, b: B.id, amount: area });
    }
  }
  return out;
}
